<template>
  <div class="notifications-promotions">
    <div class="notifications-promotions__content">
      <notifications-promotions-item
        v-for="notification in notifications"
        :key="notification.code"
        :notification="notification"
        @mark-as-read="markNotificationAsRead"
        @open-order="openOrder"
      />
      <div class="notifications-promotions__empty-block" />
    </div>

    <Teleport
      v-if="isMounted && classLocator"
      :to="teleportTarget"
    >
      <ui-loader
        v-if="isLoading"
        class="notifications-promotions__loader"
        size="60"
      />
      <div :class="['notifications-promotions__buttons', { centered: !thereAreUnreadNotifications }]">
        <span
          v-if="thereAreUnreadNotifications"
          class="notifications-promotions__button"
          @click="markAllNotificationsAsRead"
        >
          Отметить все прочитанными
        </span>
        <a
          class="notifications-promotions__button"
          href="/account/notifications"
          @click="onClickShowAllLink"
        >
          Посмотреть все
        </a>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
export default {
  name: 'notifications-promotions',
};
</script>

<script setup lang="ts">
import {
  computed, inject, onBeforeUnmount,
  onMounted, ref,
} from 'vue';
import { useStore } from 'vuex';

import { NotificationTypes } from '@/components/app-kit/notifications-promotions/constants/notifications';
import NotificationsPromotionsItem
  from '@/components/app-kit/notifications-promotions/notifications-promotions-item.vue';
import { promotionsGtmEvents } from '@/gtm/promotions-gtm-events';

import type { TNotification } from './types/Notification';

interface IProps {
  notifications: TNotification[],
  classLocator: string,
}

const isMounted = ref(false);
const $order = inject('$order');

const store = useStore();
const emit = defineEmits(['timer-is-ended']);
const props = withDefaults(defineProps<IProps>(), {
  classLocator: '',
});

const timer: ReturnType<typeof setTimeout> = setTimeout(() => {
  emit('timer-is-ended');
}, 700);

const isLoading = computed(() => store.getters['promotions/isLoading']);
const teleportTarget = computed(() => `.${props.classLocator} .control-promotions__dropdown`);
const thereAreUnreadNotifications = computed<boolean>(() => props.notifications
  .some((n) => !n.hasBeenRead && n.type !== NotificationTypes.PROMOCODE));

const unreadNotificationsIds = computed(() => props.notifications
  .filter((n) => n.type !== NotificationTypes.PROMOCODE)
  .map((n) => n.code)
  .join(','));

onMounted(() => {
  isMounted.value = true;
});

onBeforeUnmount(() => {
  clearTimeout(timer);
});

const markAllNotificationsAsRead = async () => {
  promotionsGtmEvents.onClickAllNotificationsAsRead();
  await store.dispatch('promotions/markNotificationAsRead', { ids: unreadNotificationsIds.value });
  await reloadMessages();
};

const markNotificationAsRead = async (id: string) => {
  promotionsGtmEvents.onClickNotificationsAsRead();
  await store.dispatch('promotions/markNotificationAsRead', { ids: id });
  await reloadMessages();
};

const reloadMessages = async () => {
  await store.dispatch('promotions/fetchMessages');
  await store.dispatch('promotions/fetchUnreadMessagesCount');
};

const openOrder = (orderNumber: number) => {
  $order.show(orderNumber, 'online');
};
const onClickShowAllLink = () => {
  promotionsGtmEvents.onClickNotificationsShowAllLink();
};

</script>

<style lang="scss">
.notifications-promotions {
  position: relative;

  &__content {
    max-height: 416px;
  }

  &__empty-block {
    height: 24px;
  }

  &__loader {
    position: absolute;
    min-height: 100px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: $blue;
    pointer-events: none;
  }

  &__buttons {
    position: relative;
    right: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 4px 12px 8px;
    background: $white;

    &.centered {
      justify-content: center;
    }
  }

  &__button {
    font-size: 12px;
    line-height: 20px;
    color: $blue;
    cursor: pointer;
  }
}
</style>
